/*eslint operator-assignment: off*/
import * as React from 'react'
import * as styles from '../date.pickers.styl'
import Select, { ValueType } from '../../select'
import { range } from 'lodash'
import cx from 'classnames'
import { createDateFromString, formatDateTime } from '@src/utils/date.helpers'
import { setHours, setMinutes } from 'date-fns'

interface SelectOption {
  value?: string
  label?: string
}

const formatTimeOption = (d: number) => (d <= 9 ? `0${d}` : `${d}`)
const getTimeOption = (d: number): SelectOption => ({
  value: formatTimeOption(d),
  label: formatTimeOption(d)
})

const hours = range(1, 13).map(n => getTimeOption(n))
const minutes = range(0, 60).map(n => getTimeOption(n))
const meridiens = [
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' }
]

export interface TimePickerProps {
  role?: string
  name?: string
  value: Date
  defaultValue?: Date
  disabled?: boolean
  onClick?: () => void
  onDateSelect: (date: Date) => void
}

const TimePicker: React.FC<TimePickerProps> = React.memo(
  ({
    value,
    role,
    name,
    defaultValue = new Date(),
    onClick,
    disabled,
    onDateSelect
  }: TimePickerProps) => {
    const hour = String(formatDateTime(value || defaultValue, 'hh'))
    const minute = String(formatDateTime(value || defaultValue, 'mm'))
    const meridiem = String(formatDateTime(value || defaultValue, 'a'))

    function handleHour(data: ValueType<SelectOption>) {
      const { value: hour } = data as SelectOption
      onDateSelect(setHours(value || defaultValue, Number(hour || 0)))
    }

    function handleMinute(data: ValueType<SelectOption>) {
      const { value: minute } = data as SelectOption
      onDateSelect(setMinutes(value || defaultValue, Number(minute || 0)))
    }

    function convertTime12To24(time: string) {
      let hours = Number((time || '')?.match(/^(\d+)/)[1])
      const minutes = Number((time || '')?.match(/:(\d+)/)[1])
      const AMPM = (time || '').match(/\s(.*)$/)[1]
      if (AMPM === 'PM' && hours < 12) {
        hours = hours + 12
      }
      if (AMPM === 'AM' && hours === 12) {
        hours = hours - 12
      }
      let sHours = hours?.toString()
      let sMinutes = minutes?.toString()
      if (hours < 10) sHours = '0' + sHours
      if (minutes < 10) sMinutes = '0' + sMinutes
      return sHours + ':' + sMinutes
    }

    function handleMeridiem(data: ValueType<SelectOption>) {
      const { value: meridiem } = data as SelectOption
      const oldDateString = formatDateTime(value || defaultValue, 'yyyy-MM-dd')

      const oldTimetring = formatDateTime(value || defaultValue, 'hh:mm')

      const time = convertTime12To24(`${oldTimetring} ${meridiem}`)
      const dateString = `${oldDateString} ${time} `
      onDateSelect(createDateFromString(dateString, 'yyyy-MM-dd hh:mm a'))
    }

    function handleClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
      if (onClick) {
        onClick()
      }
    }

    const hourValue = { value: hour, label: hour }
    const minuteValue = { value: minute, label: minute }
    const meridiemValue = { value: meridiem, label: meridiem }

    return (
      <div
        onClick={handleClick}
        data-name={name}
        role={role ?? `time-picker`}
        className={cx(styles.timePicker, { disabled })}
      >
        <Select
          width={'auto'}
          classNameItem={styles.selectItem}
          disablePortal
          role={'hours-select'}
          name={'hours-select'}
          onChange={handleHour}
          value={hourValue}
          options={hours}
          className={cx(styles.selectInput, 'hours-select')}
        />
        <Select
          width={'auto'}
          classNameItem={styles.selectItem}
          disablePortal
          role={'mins-select'}
          name={'mins-select'}
          onChange={handleMinute}
          value={minuteValue}
          options={minutes}
          className={cx(styles.selectInput, 'mins-select')}
        />
        <Select
          width={'auto'}
          classNameItem={styles.selectItem}
          disablePortal
          name={'mer-select'}
          role={'mer-select'}
          onChange={handleMeridiem}
          value={meridiemValue}
          options={meridiens}
          className={cx(styles.selectInput, 'mer-select')}
        />
      </div>
    )
  }
)

export default TimePicker
